import {
  FiChevronDown,
  FiChevronUp,
  FiPause,
  FiPlay,
  FiVolume2,
  FiVolumeX,
  FiX,
} from 'react-icons/fi';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  REELS_PLAY_STATUS_INDICATOR_DURATION,
  REELS_SCROLL_SWITCH_COOLDOWN,
  REELS_SWIPE_THRESHOLD,
  REELS_UNMUTE_TOAST_DURATION,
  REELS_WHEEL_SCROLL_THRESHOLD,
} from './ReelsPlayerOverlay.constants';
import styles from './ReelsPlayerOverlay.module.css';
import type { ReelsPlayerOverlayProps } from './ReelsPlayerOverlay.types';

export const ReelsPlayerOverlay: React.FC<ReelsPlayerOverlayProps> = ({
  videos,
  initialIndex,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [progress, setProgress] = useState<number>(0);
  const [showPlayStateIndicator, setShowPlayStateIndicator] =
    useState<boolean>(false);
  const [showUnmuteToast, setShowUnmuteToast] = useState<boolean>(true);

  const videoRef = useRef<HTMLVideoElement>(null);
  const lastScrollTime = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchEndY = useRef<number>(0);

  const activeVideo = videos[currentIndex];

  // Auto hide unmute toast
  useEffect(() => {
    if (!isMuted) {
      setShowUnmuteToast(false);
      return;
    }
    const timer = setTimeout(() => {
      setShowUnmuteToast(false);
    }, REELS_UNMUTE_TOAST_DURATION);
    return () => clearTimeout(timer);
  }, [isMuted, currentIndex]);

  // Navigate back and forth
  const handlePrev = useCallback((): void => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setIsPlaying(true);
      setProgress(0);
    }
  }, [currentIndex]);

  const handleNext = useCallback((): void => {
    if (currentIndex < videos.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setIsPlaying(true);
      setProgress(0);
    }
  }, [currentIndex, videos.length]);

  // Sync play/pause state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.play().catch((err) => {
        console.warn('Playback failed:', err);
        // Autoplay policy might block unmuted play, fallback to muted play
        setIsMuted(true);
        video.muted = true;
        video.play().catch((e) => console.error('Secondary play failed:', e));
      });
    } else {
      video.pause();
    }
  }, [isPlaying, currentIndex]);

  // Sync muted state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = isMuted;
  }, [isMuted, currentIndex]);

  const togglePlayPause = useCallback((): void => {
    setIsPlaying((prev) => !prev);
    setShowPlayStateIndicator(true);
    setTimeout(
      () => setShowPlayStateIndicator(false),
      REELS_PLAY_STATUS_INDICATOR_DURATION
    );
  }, []);

  const toggleMute = useCallback((e?: React.MouseEvent): void => {
    if (e) {
      e.stopPropagation();
    }
    setIsMuted((prev) => !prev);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === ' ') {
        e.preventDefault();
        togglePlayPause();
      } else if (e.key.toLowerCase() === 'm') {
        toggleMute();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext, onClose, togglePlayPause, toggleMute]);

  // Throttled mouse wheel scroll
  const handleWheel = useCallback(
    (e: React.WheelEvent): void => {
      const now = Date.now();
      if (now - lastScrollTime.current < REELS_SCROLL_SWITCH_COOLDOWN) return;

      if (e.deltaY > REELS_WHEEL_SCROLL_THRESHOLD) {
        handleNext();
        lastScrollTime.current = now;
      } else if (e.deltaY < -REELS_WHEEL_SCROLL_THRESHOLD) {
        handlePrev();
        lastScrollTime.current = now;
      }
    },
    [handleNext, handlePrev]
  );

  // Touch handlers for swipe navigation
  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    touchStartY.current = e.targetTouches[0].clientY;
    touchEndY.current = e.targetTouches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent): void => {
    touchEndY.current = e.targetTouches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((): void => {
    const diffY = touchStartY.current - touchEndY.current;

    if (diffY > REELS_SWIPE_THRESHOLD) {
      // Swipe Up -> Next Video
      handleNext();
    } else if (diffY < -REELS_SWIPE_THRESHOLD) {
      // Swipe Down -> Prev Video
      handlePrev();
    }
  }, [handleNext, handlePrev]);

  const handleTimeUpdate = useCallback((): void => {
    const video = videoRef.current;
    if (video && video.duration) {
      setProgress((video.currentTime / video.duration) * 100);
    }
  }, []);

  const handleVideoEnded = useCallback((): void => {
    if (currentIndex < videos.length - 1) {
      handleNext();
    } else {
      // Loop the last video or just pause it
      setIsPlaying(false);
    }
  }, [currentIndex, videos.length, handleNext]);

  if (!activeVideo) return null;

  return (
    <div className={styles.overlay} onClick={onClose} onWheel={handleWheel}>
      {/* Top bar with logo/title & close button */}
      <div className={styles.topHeader} onClick={(e) => e.stopPropagation()}>
        <span className={styles.logoText}>SLAYD REELS</span>
        <button
          aria-label="Close reels player"
          className={styles.closeBtn}
          onClick={onClose}
        >
          <FiX size={24} />
        </button>
      </div>

      {/* Main player viewport container */}
      <div
        className={styles.playerContainer}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className={styles.videoWrapper} onClick={togglePlayPause}>
          <video
            autoPlay
            playsInline
            className={styles.videoElement}
            loop={currentIndex === videos.length - 1} // Loop the final video if no next available
            ref={videoRef}
            src={activeVideo.videoUrl}
            onEnded={handleVideoEnded}
            onTimeUpdate={handleTimeUpdate}
          />

          {/* Large play/pause tap overlay feedback */}
          {showPlayStateIndicator && (
            <div className={styles.playStateIndicator}>
              {isPlaying ? <FiPlay size={48} /> : <FiPause size={48} />}
            </div>
          )}

          {/* Click to unmute banner prompt */}
          {showUnmuteToast && (
            <div className={styles.unmuteToast} onClick={toggleMute}>
              <FiVolumeX size={16} />
              <span>Tap to Unmute</span>
            </div>
          )}

          {/* HUD Info Overlay (bottom of video) */}
          <div className={styles.hudOverlay}>
            <div className={styles.hudInfo}>
              <span className={styles.videoTitle}>
                {activeVideo.title || `Influencer Pick ${currentIndex + 1}`}
              </span>
              <span className={styles.videoIndex}>
                {currentIndex + 1} / {videos.length}
              </span>
            </div>

            <div className={styles.hudControls}>
              <button
                aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                className={styles.iconButton}
                onClick={toggleMute}
              >
                {isMuted ? <FiVolumeX size={20} /> : <FiVolume2 size={20} />}
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className={styles.progressContainer}>
            <div
              className={styles.progressBar}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Up/Down buttons for desktop navigation inside/beside the frame */}
        <div className={styles.sideControls}>
          <button
            aria-label="Previous reel"
            className={`${styles.navArrow} ${currentIndex === 0 ? styles.disabled : ''}`}
            disabled={currentIndex === 0}
            onClick={handlePrev}
          >
            <FiChevronUp size={24} />
          </button>
          <button
            aria-label="Next reel"
            className={`${styles.navArrow} ${currentIndex === videos.length - 1 ? styles.disabled : ''}`}
            disabled={currentIndex === videos.length - 1}
            onClick={handleNext}
          >
            <FiChevronDown size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReelsPlayerOverlay;
